/* eslint-disable no-plusplus */
// anchor-typeahead.tsx
import { Component, Host, h, Prop, Watch, State, Event, EventEmitter, Element, Listen } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { parseTypeaheadOptions } from './util/parseTypeaheadOptions';
import { EventDetail, stopEvent } from '../../utils';
import { filterOptions } from './util/filterOptions';
import { TypeaheadTexts, typeaheadTextsDK, typeaheadTextsNO } from './constants/texts';
import { replacePlaceholders } from './util/replacePlaceholders';
import { IconName } from '../icon/icons';
import { OptionValue } from '../option/option';
import { cleanString } from './util/cleanString';

export type TypeaheadFilter =
  /** The option value contains the entire search string */
  | 'contains'
  /** The option value starts with the search string */
  | 'startsWith'
  /** The option value contains all the words (or partial words) in the search string, but not neccessarily in the same order */
  | 'containsWords'
  /** Implement your own filter function */
  | 'custom';

@Component({
  tag: 'anchor-typeahead',
  styleUrl: 'typeahead.scss',
  shadow: true,
})
export class Typeahead {
  /** The host element, used to query inside the component */
  @Element() private host: HTMLElement;

  /** Array of options to choose from, a JSON serialized string or an array of OptionValue */
  @Prop({ reflect: true }) options: string | OptionValue[];

  /** The id of the input field */
  @Prop() inputId?: string = `anchor-typeahead-${uuidv4()}`;

  /** The id of the input field */
  @Prop() uuid: string = uuidv4();

  /** The value of the input field */
  @Prop({ reflect: true, mutable: true }) value?: string = '';

  /**
   * Value indicating whether or not this typeahead should be disabled
   */
  @Prop({ reflect: true, mutable: false }) disabled?: boolean;

  /** Placeholder for the input field */
  @Prop({ reflect: true }) placeholder?: string | undefined = undefined;

  /** Label for the input field */
  @Prop({ reflect: true }) label?: string | undefined = undefined;

  /** The type of filter to use */
  @Prop({ reflect: true }) filter?: TypeaheadFilter = 'containsWords';

  /** Array of strings which should be treated as whitespace to make it easier to match results */
  @Prop({ reflect: true }) whitespaceCharacters?: string[] = undefined;

  /** The display language for the component. */
  @Prop({ reflect: true }) language?: 'DK' | 'NO' = 'NO';

  /** Override some, or all, texts used internally in the component */
  @Prop({ reflect: true }) texts?: string = JSON.stringify({});

  /** This prop decides if the component should show all available options before the user has typed a search phrase. */
  @Prop({ reflect: true }) showAllOptions?: boolean = true;

  /**
   * This prop decides if the messages should render between the label of an input and the input field, or below the input field.
   *
   * @type {'inside' | 'below'}
   * @default uses default from anchor-input
   */
  @Prop({ reflect: true }) messagePlacement?: 'inside' | 'below' | undefined;

  /**
   * When this is true, the component will have a maximum number of options to show, determined by the numberOfOptionsToDisplay prop.
   *
   * Default value is false.
   */
  @Prop({ reflect: true, mutable: true }) limitNumberOfVisibleOptions: boolean = false;

  /**
   * The maximum number of options to display when limitNumberOfVisibleOptions is true.
   *
   * Default value is 100.
   */
  @Prop({ reflect: true, mutable: true }) numberOfOptionsToDisplay: number = 100;

  /** Options parsed as objects */
  @State() parsedOptions: OptionValue[] = [];

  /** array of matched objects */
  @State() matches: OptionValue[] = [];

  /** index of focused option */
  @State() focusedIndex: number = -1;

  /** The currently selected option */
  @State() selectedOption: OptionValue | undefined;

  /** Texts used internally in the typeahead */
  @State() typeaheadTexts: TypeaheadTexts;

  /** When this is true, the list of options is visible */
  @State() expanded: boolean = false;

  /**
   * Show an error message
   */
  @Prop() errorProp: string = '';

  /**
   * Called every time the component is connected to the DOM.
   */
  componentWillLoad() {
    this.watchOptions(this.options);
    this.watchLanguage(this.language);
    this.watchTexts(this.texts);
    this.matches = this.getDefaultMatches();
    // Set selected option initially if the given value matches an option
    this.selectedOption = this.parsedOptions.find(option => option.value === this.value);
  }

  /** Event emitted when a valid option has been selected */
  @Event() selected: EventEmitter;

  /**
   *  Event emitted when the dropdown select expands
   */
  @Event() expand: EventEmitter;

  /**
   *  Event emitted when the dropdown select collapses
   */
  @Event() collapse: EventEmitter;

  /** @deprecated this event has been replaced by the selected event. inputChange is emitted by the underlying anchor-input. Will be removed 20.12.2023 */
  @Event() inputChange: EventEmitter;

  async expandComponent(e?: Event): Promise<boolean> {
    if (e) {
      stopEvent(e);
    }
    if (this.disabled) {
      return false;
    }
    if (!this.expanded) {
      this.expanded = true;
      this.expand.emit(e);
    }
    return true;
  }

  async collapseComponent(e?: Event): Promise<boolean> {
    if (e) {
      stopEvent(e);
    }
    if (this.disabled) {
      return false;
    }
    if (this.expanded) {
      this.expanded = false;
      this.collapse.emit(e);
    }
    // Restore selected value or clear search field when component is collapsed
    if (this.selectedOption) {
      this.value = this.selectedOption.label;
    } else {
      this.value = '';
    }
    return true;
  }

  @Watch('value')
  updateMatches() {
    const filterValue = cleanString(this.value ?? '', this.whitespaceCharacters);
    if (!filterValue || !Array.isArray(this.parsedOptions) || this.parsedOptions.length === 0) {
      this.matches = this.getDefaultMatches();
    } else {
      this.matches = filterOptions({ filterValue, parsedOptions: this.parsedOptions, filter: this.filter, whitespaceCharacters: this.whitespaceCharacters }).matches;
    }
  }

  /**
   *  Handles conversion between strings and arrays of strings after the initial render if the value passed in options changes
   */
  @Watch('options')
  watchOptions(options: string | OptionValue[]) {
    if (options) {
      this.parsedOptions = parseTypeaheadOptions(options);
    }
  }

  @Watch('texts')
  watchTexts(texts: string) {
    const parsedTexts = parseTypeaheadOptions(texts);
    this.typeaheadTexts = {
      ...this.typeaheadTexts,
      ...parsedTexts,
    };
  }

  @Watch('language')
  watchLanguage(language: 'DK' | 'NO') {
    const parsedTexts = parseTypeaheadOptions(this.texts);
    let defaultTexts: TypeaheadTexts = typeaheadTextsNO;
    switch (language) {
      case 'DK': {
        defaultTexts = typeaheadTextsDK;
        break;
      }
      case 'NO':
      default: {
        defaultTexts = typeaheadTextsNO;
        break;
      }
    }
    this.typeaheadTexts = {
      ...defaultTexts,
      ...parsedTexts,
    };
  }

  @Watch('focusedIndex')
  watchFocusedIndex(newIndex: number) {
    const menu = this.host.shadowRoot.querySelector('.menu');
    const activeElement = menu.querySelector(`.option--${newIndex}`);
    activeElement?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
  }

  /** Get the list of matches to display when the search field is empty */
  getDefaultMatches() {
    if (this.showAllOptions) {
      return this.parsedOptions;
    }
    return [];
  }

  @Listen('inputevent')
  handleInput(e: CustomEvent<EventDetail>) {
    const query = (e.detail as EventDetail).value;
    this.value = query;
    const cleanQuery = cleanString(query);
    if (cleanQuery) {
      this.focusedIndex = -1; // reset focusedIndex when input changes
    } else {
      this.matches = this.getDefaultMatches();
    }
  }

  @Listen('click', { target: 'window', capture: true })
  handleClickOutside(e: PointerEvent) {
    if (this.expanded && !this.host.contains(e.target as HTMLElement)) {
      return { event: e, success: this.expanded && this.collapseComponent() };
    }

    return { event: e, success: false };
  }

  @Listen('click')
  handleClick(e: PointerEvent) {
    const targetIsHostElement = e.target === this.host;
    const targetIsSlotContent = !targetIsHostElement;
    if (targetIsSlotContent) {
      return { event: e, success: targetIsSlotContent };
    }
    return { event: e, success: this.expanded ? this.collapseComponent(e) : this.expandComponent(e) };
  }

  handleKeyDown(e: KeyboardEvent) {
    const { code } = e;
    switch (code) {
      case 'ArrowUp': {
        stopEvent(e);
        if (this.focusedIndex > -1) {
          this.focusedIndex--;
        }
        break;
      }
      case 'ArrowDown': {
        stopEvent(e);
        if (this.focusedIndex < this.matches.length - 1) {
          this.focusedIndex++;
        }
        break;
      }
      case 'Enter':
      case 'NumpadEnter': {
        stopEvent(e);
        if (this.focusedIndex > -1 && this.focusedIndex < this.matches.length) {
          const match = this.matches[this.focusedIndex];
          this.handleSelect(match);
        } else if (this.matches.length === 1) {
          this.handleSelect(this.matches[0]);
        }
        break;
      }
      case 'Escape': {
        stopEvent(e);
        this.collapseComponent();
        break;
      }
      case 'Tab': {
        this.collapseComponent();
        break;
      }
      default: {
        break;
      }
    }
  }

  handleKeyUp(e: KeyboardEvent) {
    const { code } = e;
    switch (code) {
      case 'ArrowUp':
      case 'ArrowDown':
      case 'Enter':
      case 'NumpadEnter':
      case 'Escape':
      case 'Tab': {
        break;
      }
      default: {
        if (this.value.length > 0) {
          this.expandComponent();
        }
        break;
      }
    }
  }

  handleSelect(option: OptionValue) {
    if (this.disabled) {
      return;
    }
    this.value = option.label; // set value to selected option
    this.selectedOption = option;
    this.selected.emit(option); // emit event with selected option
    this.inputChange.emit(option);
    this.matches = []; // clear all matches to close the dropdown
    this.focusedIndex = -1; // reset focusedIndex after selection
  }

  getIcon(): IconName | undefined {
    if (!this.showAllOptions) {
      return undefined;
    }
    return this.expanded ? 'chevron-up' : 'chevron-down';
  }

  valueMatchesOneOrMoreOptions(): boolean {
    if (!this.value) {
      return false;
    }
    const filteredOptions = filterOptions({
      filterValue: this.value,
      parsedOptions: this.parsedOptions,
      filter: this.filter,
      whitespaceCharacters: this.whitespaceCharacters,
    }).matches;
    const numberOfFilteredOptions = filteredOptions.length;

    return numberOfFilteredOptions >= 1;
  }

  getVisibleMatches(): OptionValue[] {
    if (!this.limitNumberOfVisibleOptions) {
      return this.matches;
    }
    return this.matches.slice(0, this.numberOfOptionsToDisplay);
  }

  handleFocusEvent(_e: CustomEvent) {
    /** When showAllOptions is true the typeahead should work more like a select.
     * In this case we want to show the list of options when the field is focused.
     * If the field has no search value, or the list of matches is empty when the field is focused
     * we should show all potential options.
     * This makes it much easier for users to change which value they have selected, if they misclicked
     * or simply changed their minds.
     */
    if (this.showAllOptions && (this.value === '' || !!this.parsedOptions.find(option => option.label === this.value))) {
      this.matches = this.getDefaultMatches();
    }
  }

  render() {
    return (
      <Host>
        <div id={`${this.inputId}__container`} class={{ 'container': true, 'container--disabled': this.disabled, 'container--expanded': this.expanded }}>
          <anchor-input
            inputId={this.inputId}
            type="text"
            value={this.value}
            placeholder={this.placeholder}
            onKeyDown={e => this.handleKeyDown(e)}
            onKeyUp={e => this.handleKeyUp(e)}
            messagePlacement={this.messagePlacement}
            onFocusevent={e => this.handleFocusEvent(e)}
            readonly={this.disabled}
            select-on-focus="true"
            tabindex="0"
            aria-live="polite"
            aria-describedby={`description-${this.uuid}`}
            aria-activedescendant={`${this.inputId}--${this.focusedIndex}`}
            role="combobox"
            icon={this.getIcon()}
            errorProp={this.errorProp}
          >
            <slot name="label">{this.label}</slot>
            <span slot="label-suffix">
              <slot name="label-suffix" />
            </span>
          </anchor-input>

          {(this.matches.length ?? 0) > 0 && (
            <ul
              class={`menu menu--results ${this.errorProp ? 'has-error' : ''}`}
              role="listbox"
              aria-label={replacePlaceholders(this.typeaheadTexts.screenReaderFocusedResultInList, {
                index: this.focusedIndex + 1,
                length: this.matches.length,
                result: this.matches[this.focusedIndex]?.label,
              })}
            >
              {this.getVisibleMatches().map((option, index) => (
                <li>
                  <anchor-option
                    itemId={`${this.inputId}--${index}`}
                    class={`option option--${index} ${index === this.focusedIndex ? 'active' : ''}`}
                    focused={index === this.focusedIndex}
                    onClick={() => this.handleSelect(option)}
                    aria-label={replacePlaceholders(this.typeaheadTexts.screenReaderFocusedResultInList, {
                      index: index + 1,
                      length: this.matches.length,
                      result: option?.label,
                    })}
                    value={option.value}
                    label={option.label}
                    tabIndex={0}
                    role="option"
                  />
                </li>
              ))}
              {this.limitNumberOfVisibleOptions && this.matches.length > this.numberOfOptionsToDisplay ? (
                <li>
                  <anchor-option
                    itemId={`${this.inputId}--overflow`}
                    class={`option option--overflow`}
                    focused={false}
                    value="typeahead-list-overflow-option"
                    label={replacePlaceholders(this.typeaheadTexts.overflowResultMessage, {
                      length: this.matches.length - this.numberOfOptionsToDisplay,
                    })}
                    tabIndex={0}
                    role="button"
                  />
                </li>
              ) : undefined}
            </ul>
          )}
          {this.matches.length === 0 && Boolean(this.value) && !this.valueMatchesOneOrMoreOptions() && (
            <ul class="menu menu--no-results" role="listbox" aria-label={this.typeaheadTexts.screenReaderNoResult}>
              <li>
                <anchor-option
                  itemId={`${this.inputId}--no-results`}
                  value=""
                  label={this.typeaheadTexts.noResult}
                  disabled
                  class={`option option--no-results`}
                  focused={false}
                  tabindex="0"
                ></anchor-option>
              </li>
            </ul>
          )}
          <div id={`description-${this.uuid}`} class="sr-only">
            {!this.value ? this.typeaheadTexts.screenReaderDescription : undefined}
            {this.value && this.matches.length === 0 ? <span>{this.typeaheadTexts.screenReaderNoResult}</span> : undefined}
            {this.value && this.matches.length > 0 && this.focusedIndex >= 0 ? (
              <span>
                {replacePlaceholders(this.typeaheadTexts.screenReaderFocusedResultInList, {
                  index: this.focusedIndex + 1,
                  length: this.matches.length,
                  result: this.matches[this.focusedIndex]?.label,
                })}
              </span>
            ) : undefined}
            {this.value && this.matches.length > 0 && this.focusedIndex === -1 ? (
              <span>
                {replacePlaceholders(this.typeaheadTexts.screenReaderResultList, {
                  length: this.matches.length,
                })}
              </span>
            ) : undefined}
          </div>
        </div>
      </Host>
    );
  }
}
